import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';

import { toastError, toastSuccess } from '@components/Toast';
import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import useDealSplitPrompt, { dealSplitQuery } from '@components/modals/useDealSplitPrompt';

import OrderCancelBottomBar from '@features/order-cancel/OrderCancelBottomBar';
import PlaceOrderConfirmation from '@features/order/components/orderInfo/PlaceOrderConfirmation';
import DesignsByCollection from '@features/order/DesignsByCollection';
import {
	validateOrderStyles,
	handleValidationErrors,
	validateHeaderFields,
	getFirstUnitPriceInvalidStyle,
	normalizeSegmentationType,
	getAutomationBuyerPriceValues,
	getOrderStylesGroupData
} from '@features/order/utils/automationIndex';
import ColorSizeBreakDownTable from '@features/order/components/orderInfo/ColorSizeBreakDownTable';
import OrderCancelInfo from '@features/order-cancel/OrderCancelInfo';
import EtdApprovalInfo from '@features/order-cancel/EtdApprovalInfo';
import EtdUpdateLog from '@features/order/components/orderInfo/EtdUpdateLog';
import EtdUpdateTimeline from '@features/order/components/orderInfo/EtdUpdateTimeline';
import {
	useCreateProgramTypeMutation,
	useLazyGetAllProgramTypesQuery,
	useUpdateProgramTypeMutation
} from '@features/order/api/OrderDetailsApi';
import {
	useSetExtractPoDocumentsMutation,
	useGetCollectionsQuery,
	useGetFolderCollectionsQuery
} from '@features/order/api/PoAutomationApi';

import { fetchOrderDetailsInfo } from '@store/orders/actions';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { getOrderDetailsInfo } from '@store/reducers';

import { fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import {
	isAdmin,
	packTag,
	renderSetImages,
	setTag,
	createTnaTag,
	splitTag,
	STATIC_SET_CONSTANTS,
	validateNumber,
	validateFloatNumber
} from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

import { useGetProjectDealListQuery } from '@/features/sampleDevelopment/api/sampleDevelopmentApi';

const mapAutomationSizes = (sizes, idx) => {
	if (!Array.isArray(sizes)) return [];

	return sizes
		.filter((s) => s && typeof s === 'object')
		.map((size, sizeIdx) => ({
			id: size?.productSizeId || size?.id || `design_${idx}_size_${sizeIdx}`,
			label: size?.sizeLabel || '',
			sizeLabel: size?.sizeLabel || '',
			quantity: size?.quantity || 0,
			price: size?.price || 0,
			ean: size?.ean || '',
			sku: size?.sku || '',
			serial: size?.serial || 0
		}));
};

const mapAutomationColors = (colors, idx) => {
	if (!Array.isArray(colors)) return [];

	const usedIds = new Set();

	return colors
		.filter((c) => c && typeof c === 'object')
		.map((color, colorIdx) => {
			const rawId = color?.id;
			const hasId = rawId !== null && rawId !== undefined && `${rawId}`.trim() !== '';
			const fallbackId = `design_${idx}_color_${colorIdx}`;

			// A missing id, or one the extraction already used on an earlier row, falls back to
			// the row position; the suffix only matters if a real id looks like a fallback id.
			let id = hasId && !usedIds.has(`${rawId}`) ? rawId : fallbackId;
			let suffix = 0;
			while (usedIds.has(`${id}`)) {
				suffix += 1;
				id = `${fallbackId}_${suffix}`;
			}
			usedIds.add(`${id}`);

			return {
				id,
				name: '',
				colorName: '',
				// pre-fills the title in the edit-color popup; the name itself stays blank until the color is saved
				extractedColorName: color?.colorName || '',
				colorCode: '',
				hexCode: color?.hexCode || '',
				colorType: color?.colorType || 'SOLID',
				compositeColorList: color?.compositeColorList || [],
				sizeQuantityPairList: mapAutomationSizes(color?.sizes, idx)
			};
		});
};

// Turns one design of a PO extraction response into the style shape the order tables render.
export const mapAutomationDesignToStyle = (
	item,
	idx,
	{ basicInfoData, orderDetailsInfo, brandId, extractionResultId, isDrafted } = {}
) => {
	if (!item || typeof item !== 'object') return null;

	const colorList = mapAutomationColors(item?.colors, idx);

	// sizeId -> sku
	const sizeWisePoItemIdentifier = colorList.reduce((acc, color) => {
		(color?.sizeQuantityPairList || []).forEach((size) => {
			if (size?.id && size?.sku && !acc[size.id]) acc[size.id] = size.sku;
		});
		return acc;
	}, {});

	// colorId -> first sku of that color
	const colorWisePoItemIdentifierFromSku = colorList.reduce((acc, color) => {
		const firstSku = color?.sizeQuantityPairList?.find((s) => s?.sku)?.sku;
		if (color?.id && firstSku) acc[color.id] = firstSku;
		return acc;
	}, {});

	const firstAvailableSku = Object.values(colorWisePoItemIdentifierFromSku)[0] || null;
	const { normalizedBuyerQuotationType, colorWiseBuyerPrice, sizeWiseBuyerPrice, designWiseBuyerPrice } =
		getAutomationBuyerPriceValues({
			colorList,
			buyerQuotationType: item?.buyerQuotationType,
			colorWiseBuyerPrice: item?.colorWiseBuyerPrice,
			sizeWiseBuyerPrice: item?.sizeWiseBuyerPrice,
			designWiseBuyerPrice: item?.designWiseBuyerPrice
		});

	const normalizedPoItemIdentifierType = item?.poItemIdentifierType
		? normalizeSegmentationType(item.poItemIdentifierType)
		: 'NOT_AVAILABLE';

	return {
		...item,
		id: item?.id || `design_${idx}`,
		productId: item?.productId || `product_${idx}`,
		name: item?.name || `Design ${idx}`,
		referenceNumber: item?.referenceNumber || item?.productReferenceNumber || '',
		priceType:
			item?.priceType ||
			basicInfoData?.selectedIncoTerm?.value ||
			basicInfoData?.selectedIncoTerm?.id ||
			orderDetailsInfo?.incoterms ||
			'',
		poNumber: item?.poNumber || '',
		poReceiveDate:
			item?.poReceivedDate || item?.poReceiveDate || basicInfoData?.projectDetails?.startDate || '',
		designIdentifier: item?.designIdentifier,
		deliveryDate:
			item?.buyerEtd || item?.deliveryDate || basicInfoData?.projectDetails?.deliveryDate || '',
		supplierName: item?.supplierName || '',
		supplierId: item?.supplierId || 0,
		projectDeal: item?.projectDeal || basicInfoData?.selectedDeal || null,
		productSubCategoryName: item?.productSubCategoryName,
		isEdit: isDrafted,
		isBaseRfq: true,
		isPiCreated: false,
		isScCreated: false,
		isTNACreated: false,
		status: 'PENDING',
		draftStatus: 'COMPLETED',
		canceledStatus: null,
		colorWiseSizeQuantityPairList: colorList || [],
		poItemIdentifierType: normalizedPoItemIdentifierType,
		sizeWisePoItemIdentifier: {
			...sizeWisePoItemIdentifier,
			...(item?.sizeWisePoItemIdentifier || {})
		},
		colorWisePoItemIdentifier: {
			...colorWisePoItemIdentifierFromSku,
			...(item?.colorWisePoItemIdentifier || {})
		},
		designWisePoItemIdentifier: item?.designWisePoItemIdentifier || firstAvailableSku,
		colorWiseBuyerPrice: colorWiseBuyerPrice,
		sizeWiseBuyerPrice: sizeWiseBuyerPrice,
		designWiseBuyerPrice: designWiseBuyerPrice,
		buyerQuotationType: normalizedBuyerQuotationType,
		documentResponseList: item?.documentResponseList || [],
		docUrl: item?.docUrl || item?.documentResponseList?.[0]?.docUrl || '',
		brandId: item?.brandId || orderDetailsInfo?.brandResponse?.id || brandId,
		buyerId: item?.buyerId || orderDetailsInfo?.buyerResponse?.id,
		extractionResultId: extractionResultId,
		productColorIds: [],
		deliveryDateChangeResponseList: [],
		etdChangePending: false
	};
};

const OrderStyles = ({
	orderId,
	isDrafted,
	type,
	productRefNumber,
	hideStatusType,
	addNewStyle,
	poItemIdentifier,
	orderCancelInfo,
	getOrderCancelInfo,
	pOSegmentationType,
	setOpenOrderDesignModal,
	stylesCount,
	automationDesigns,
	collectionId,
	folderId,
	poSubmitButton,
	poDocIdList,
	basicInfoData,
	brandId = null,
	extractionResultId = null,
	isFromPoAutomation = false
}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { askDealSplit, dealSplitModal } = useDealSplitPrompt();
	const [orderStyles, setOrderStyles] = useState([]);
	const [isOverflow, setIsOverflow] = useState(false);
	const [, setLoading] = useState(false);
	const [groupData, setGroupData] = useState(null);
	const [, setSeasons] = useState([]);
	const [, setSetPackStyleFlag] = useState([]);
	const [singleSetPackStyle] = useState('');
	const [designIdList, setDesignIdList] = useState([]);
	const [, setRemovedColorIds] = useState([]);
	const [, setProductWiseColorIds] = useState({});
	const [selectedRFQIds, setSelectedRFQIds] = useState([]);
	const [error, setError] = useState('');
	const [errorDesign, setErrorDesign] = useState({});
	const [show, setShow] = useState(false);
	const [isPlacingOrder, setIsPlacingOrder] = useState(false);
	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [selectedStyleItem, setSelectedStyleItem] = useState(null);
	const [etdApprovalRequests, setEtdApprovalRequests] = useState([]);
	const [isEtdApprovalRoute, setIsEtdApprovalRoute] = useState(false);
	const [, setOpenAccordion] = useState([]);
	const [errors, setErrors] = useState({});
	const buttonRef = useRef();
	const orderDetailsInfo = useSelector(getOrderDetailsInfo);
	const [selectedPo, setSelectedPo] = useState(null);
	const [programTypeOptionList, setProgramTypeOptionList] = useState([]);
	const [showProgramTypeModal, setShowProgramTypeModal] = useState(false);
	const [selectedProgramType, setSelectedProgramType] = useState(null);
	const [programTypeName, setProgramTypeName] = useState('');
	const [isProgramTypeSubmitting, setIsProgramTypeSubmitting] = useState(false);
	const [programTypeErrors, setProgramTypeErrors] = useState({});
	const [salesMarginErrors, setSalesMarginErrors] = useState({});
	const [fetchProgramTypeTrigger] = useLazyGetAllProgramTypesQuery();
	const [createProgramType] = useCreateProgramTypeMutation();
	const [updateProgramType] = useUpdateProgramTypeMutation();
	const [setExtractPoDocuments] = useSetExtractPoDocumentsMutation();
	const { data: projectDealList } = useGetProjectDealListQuery(
		{
			params: {
				brandId: orderDetailsInfo?.brandResponse?.id || brandId
			}
		},
		{
			skip: !orderDetailsInfo?.brandResponse?.id && !brandId
		}
	);

	// styles can be added to the collection/folder from elsewhere, so don't serve a stale cached list
	const { data: collectionData } = useGetCollectionsQuery(
		{ collectionId },
		{ skip: !collectionId, refetchOnMountOrArgChange: true, refetchOnFocus: true }
	);
	const { data: folderCollectionData } = useGetFolderCollectionsQuery(
		{ folderId },
		{ skip: !folderId, refetchOnMountOrArgChange: true, refetchOnFocus: true }
	);

	const isMissingProductId = automationDesigns?.some((item) => !item?.productId);

	// Handle automationDesigns data when available
	useEffect(() => {
		if (automationDesigns && Array.isArray(automationDesigns) && automationDesigns.length > 0) {
			try {
				const processedDesigns = automationDesigns
					.map((item, idx) =>
						mapAutomationDesignToStyle(item, idx, {
							basicInfoData,
							orderDetailsInfo,
							brandId,
							extractionResultId,
							isDrafted
						})
					)
					.filter((item) => item !== null);

				if (processedDesigns.length > 0) {
					setOrderStyles(processedDesigns);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error processing automationDesigns:', error);
				setOrderStyles([]);
			}
		}
	}, [automationDesigns, orderDetailsInfo?.incoterms]);

	// Applies the header incoterm to every style without rebuilding them.
	useEffect(() => {
		const incoTerm = basicInfoData?.selectedIncoTerm?.value || basicInfoData?.selectedIncoTerm?.id;
		if (!incoTerm) return;
		setOrderStyles((prev) =>
			prev.some((style) => style.priceType !== incoTerm)
				? prev.map((style) => ({ ...style, priceType: incoTerm }))
				: prev
		);
	}, [basicInfoData?.selectedIncoTerm?.value, basicInfoData?.selectedIncoTerm?.id]);

	// Applies the header PO date to every style the same way, skipping any style whose PO date is
	useEffect(() => {
		const poReceiveDate = basicInfoData?.projectDetails?.startDate;
		if (!poReceiveDate) return;
		setOrderStyles((prev) =>
			prev.some((style) => style.poReceiveDate !== poReceiveDate)
				? prev.map((style) => ({ ...style, poReceiveDate }))
				: prev
		);
	}, [basicInfoData?.projectDetails?.startDate]);

	// Applies the header ETD to every style the same way, skipping any style whose ETD is
	useEffect(() => {
		const deliveryDate = basicInfoData?.projectDetails?.deliveryDate;
		if (!deliveryDate) return;
		setOrderStyles((prev) =>
			prev.some((style) => !style.etdChangePending && style.deliveryDate !== deliveryDate)
				? prev.map((style) => (style.etdChangePending ? style : { ...style, deliveryDate }))
				: prev
		);
	}, [basicInfoData?.projectDetails?.deliveryDate]);

	// Applies the header deal to every style the same way.
	useEffect(() => {
		const headerDeal = basicInfoData?.selectedDeal;
		if (!headerDeal?.id) return;
		setOrderStyles((prev) =>
			prev.some((style) => style.projectDeal?.id !== headerDeal.id)
				? prev.map((style) => ({ ...style, projectDeal: headerDeal, dealId: headerDeal.id }))
				: prev
		);
	}, [basicInfoData?.selectedDeal?.id, basicInfoData?.selectedDeal?.dealName]);

	const programTypeOptions = useMemo(() => {
		if (!Array.isArray(programTypeOptionList)) {
			return [];
		}
		return programTypeOptionList
			.map((item) => ({
				id: item?.id,
				name: item?.name
			}))
			.filter((item) => item.id && item.name);
	}, [programTypeOptionList]);

	useEffect(() => {
		const allProductRefNumbers = groupData?.map((item) => item.productReferenceNumber);
		setOpenAccordion(allProductRefNumbers);
	}, [groupData]);

	const getProductIds = (idList) => {
		return idList?.map((item) => item.productId);
	};

	const getStyleOrder = (item) =>
		Number(item?.sequence ?? item?.displayOrder ?? item?.lineNumber ?? item?.id ?? item?.rfqId ?? 0) || 0;

	const sortStylesForDisplay = (styles = []) =>
		[...styles].sort((a, b) => getStyleOrder(a) - getStyleOrder(b));

	const productIds = useMemo(() => getProductIds(orderStyles), [orderStyles]);

	useEffect(() => {
		if (productRefNumber) {
			const element = document.getElementById(productRefNumber);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}, [productRefNumber]);

	useEffect(() => {
		if (orderStyles) {
			let ids = orderStyles.map((item) => item.productId);
			setDesignIdList(ids);
			if (!isDrafted) {
				let quotationColorIds = [];
				let productColorIds = [];
				orderStyles
					.map((styleItem) => styleItem.colorWiseSizeQuantityPairList)
					.forEach((colorItem) => {
						if (colorItem) {
							quotationColorIds.push(...colorItem.map((singleItem) => singleItem.id));
						}
					});
				orderStyles
					.map((styleItem) => styleItem?.productColorIds)
					.forEach((colorIds) => {
						if (colorIds) {
							productColorIds.push(...colorIds);
						}
					});

				let removedColorIds = productColorIds
					.filter((id) => !quotationColorIds.includes(id))
					.map((id) => parseInt(id));
				setRemovedColorIds(removedColorIds);
			} else {
				let productWiseColorIds = {};
				orderStyles.map((styleItem) => {
					productWiseColorIds[styleItem.productId] = styleItem.colorWiseSizeQuantityPairList?.map(
						(colorId) => colorId.id
					);
				});
				setProductWiseColorIds(productWiseColorIds);
			}
		}
	}, [orderStyles]);

	useEffect(() => {
		setGroupData(getOrderStylesGroupData(filterStylesData()));
	}, [orderStyles, hideStatusType]);

	useEffect(() => {
		if (isFromPoAutomation) return;
		getOrderQuotes(orderId);
	}, [orderId, isFromPoAutomation]);

	// An order can have several styles waiting for ETD approval, so the whole set is
	// fetched for the order and rendered below each affected style.
	const getEtdApprovalInfo = () => {
		if (!orderId) return;
		ApiClient.GET('rfqChangeApprovalRequestsByOrder', `/${orderId}`)
			.then(({ data }) => {
				setEtdApprovalRequests(
					Array.isArray(data) ? data.filter((request) => request?.approvalType === 'ETD') : []
				);
			})
			.catch(() => {
				setEtdApprovalRequests([]);
			});
	};

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const routeFrom = params.get('routeFrom');
		if (routeFrom === 'ETD_APPROVAL') {
			setIsEtdApprovalRoute(true);
			getEtdApprovalInfo();
		}
	}, [orderId]);

	const rfqIdWiseEtdApproval = useMemo(() => {
		const map = {};
		etdApprovalRequests.forEach((request) => {
			if (request?.rfqId) {
				map[request.rfqId] = request;
			}
		});
		return map;
	}, [etdApprovalRequests]);

	const hasEtdApprovalHighlight = isEtdApprovalRoute && etdApprovalRequests.length > 0;

	const getProgramTypePayload = (response) => {
		if (Array.isArray(response)) return response;
		if (Array.isArray(response?.data)) return response.data;
		return [];
	};

	const fetchProgramTypes = async () => {
		try {
			const response = await fetchProgramTypeTrigger().unwrap();
			setProgramTypeOptionList(getProgramTypePayload(response));
		} catch (error) {
			toastError(error?.data?.message || 'Failed to fetch program types.');
		}
	};

	useEffect(() => {
		fetchProgramTypes();
	}, []);

	const onSave = () => {
		const missingProgramTypes = {};
		const missingSalesMargins = {};
		orderStyles.forEach((item) => {
			if (!item.programTypeId && !item.programType?.id) {
				missingProgramTypes[item.id] = 'Program type is mandatory';
			}
			if (
				(item.salesMarginPercentage === null ||
					item.salesMarginPercentage === undefined ||
					item.salesMarginPercentage === '') &&
				(item.salesMarginFobAmount === null ||
					item.salesMarginFobAmount === undefined ||
					item.salesMarginFobAmount === '')
			) {
				missingSalesMargins[item.id] = 'Sales margin is mandatory';
			}
		});
		if (Object.keys(missingProgramTypes).length > 0) {
			setProgramTypeErrors(missingProgramTypes);
			return;
		}
		setProgramTypeErrors({});
		if (Object.keys(missingSalesMargins).length > 0) {
			setSalesMarginErrors(missingSalesMargins);
			return;
		}
		setSalesMarginErrors({});
		setShow(true);
	};

	const createOrder = async () => {
		const splitDealIds = await askDealSplit(orderId, { submitting: true });
		if (splitDealIds === null) return;

		setIsPlacingOrder(true);
		try {
			setLoading(true);
			const { data } = await ApiClient.POST(
				'submitDraftedOrder',
				{},
				`${orderId}/submit-drafted-order${dealSplitQuery(splitDealIds)}`
			);
			setIsPlacingOrder(false);
			if (data?.success) {
				toastSuccess(data.message);
				dispatch(fetchOrderDetailsInfo(orderId));
				setShow(false);
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			setIsPlacingOrder(false);
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			setIsPlacingOrder(false);
			setLoading(false);
		}
	};

	const onPoSubmit = async () => {
		try {
			dispatch(openLoader());
			setIsPlacingOrder(true);

			const stopPoSubmitWithError = (message) => {
				toastError(message);
				dispatch(closeLoader());
				setIsPlacingOrder(false);
			};

			const revenueStream =
				basicInfoData?.selectedSalesChannel?.id || basicInfoData?.selectedSalesChannel?.value || '';

			// Validate required header fields
			const headerError = validateHeaderFields({ basicInfoData });
			if (headerError) {
				stopPoSubmitWithError(headerError);
				return;
			}

			if (orderStyles.some((style) => !(style?.projectDeal?.id || style?.dealId))) {
				stopPoSubmitWithError('Deal is required');
				return;
			}

			if (document.querySelector('.miss-match-size-category')) {
				stopPoSubmitWithError(
					'Size category mismatch found. Please fix the highlighted sizes before submitting.'
				);
				return;
			}

			if (orderStyles.some((style) => !style?.deliveryDate)) {
				stopPoSubmitWithError('Delivery date is required');
				return;
			}
			// Validate all orderStyles before submitting
			const { missingProgramTypes, missingSalesMargins } = validateOrderStyles(orderStyles);

			const hasErrors = handleValidationErrors({
				missingProgramTypes,
				missingSalesMargins,
				setProgramTypeErrors,
				setSalesMarginErrors,
				dispatch,
				closeLoader,
				setIsPlacingOrder,
				toastError
			});

			if (hasErrors) {
				return;
			}

			// if (isMissingProductId) {
			// 	toastError('Product ID is missing. Please select designs and try again.');
			// 	return;
			// }

			const invalidUnitPriceStyle = getFirstUnitPriceInvalidStyle(orderStyles);
			if (invalidUnitPriceStyle) {
				setErrorDesign({
					totalError: 'Unit price is required',
					designId: invalidUnitPriceStyle.id
				});
				stopPoSubmitWithError('Unit price is required');
				return;
			}
			setErrorDesign({});

			// Prepare designs array with colors and sizes
			const designs = orderStyles.map((style) => ({
				productId: style.productId,
				isBaseRfq: style?.isBaseRfq ?? true,
				referenceNumber: style.referenceNumber || style.productReferenceNumber,
				poNumber: style.poNumber,
				poReceiveDate: style.poReceiveDate || basicInfoData?.projectDetails?.startDate || '',
				deliveryDate: style.deliveryDate || basicInfoData?.projectDetails?.deliveryDate || '',
				priceType:
					style.priceType ||
					basicInfoData?.selectedIncoTerm?.value ||
					basicInfoData?.selectedIncoTerm?.id ||
					'',
				dealId: style.projectDeal?.id || style.dealId || basicInfoData?.selectedDeal?.id,
				programTypeId: style.programType?.id || style.programTypeId,
				...(style.salesMarginFobAmount !== null &&
				style.salesMarginFobAmount !== undefined &&
				style.salesMarginFobAmount !== ''
					? { salesMarginFobAmount: style.salesMarginFobAmount }
					: { salesMarginPercentage: style.salesMarginPercentage }),
				unitOfMeasurementQty: style.unitOfMeasurementQty,
				designIdentifier: style.designIdentifier,
				colors:
					style.colorWiseSizeQuantityPairList?.map((color) => ({
						colorId: color.id || color.selectedPantoneColorId || color.pantoneColorId,
						colorName: color.colorName || color.name,
						sizes:
							color.sizeQuantityPairList?.map((size) => ({
								productSizeId: size.id,
								sizeLabel: size.sizeLabel || size.label,
								sku: size.sku,
								quantity: size.quantity || 0,
								price: Number(
									style?.buyerQuotationType === 'SIZEWISE'
										? (style?.sizeWiseBuyerPrice?.[size.id] ?? size.price ?? 0)
										: style?.buyerQuotationType === 'COLORWISE'
											? (style?.colorWiseBuyerPrice?.[color.id] ?? size.price ?? 0)
											: style?.buyerQuotationType === 'DESIGNWISE'
												? (style?.designWiseBuyerPrice ?? size.price ?? 0)
												: (size.price ?? 0)
								)
							})) || []
					})) || []
			}));

			const requestBody = {
				buyerId:
					basicInfoData?.selectedBuyer?.id ||
					basicInfoData?.projectDetails?.buyerResponse?.id ||
					orderStyles[0]?.buyerId ||
					orderDetailsInfo?.buyerResponse?.id ||
					null,
				collectionId: collectionId || orderDetailsInfo?.collectionId || null,
				folderId: folderId || orderDetailsInfo?.folderId || null,
				title: basicInfoData?.projectDetails?.name || orderDetailsInfo?.projectDetails?.name || '',
				projectManagerId:
					basicInfoData?.selectedProjectManager?.value ||
					orderDetailsInfo?.projectManagerId ||
					orderDetailsInfo?.projectManagerResponse?.id ||
					0,
				accountManagerId:
					basicInfoData?.selectedAccountManager?.value ||
					basicInfoData?.selectedAccountManager?.id ||
					orderDetailsInfo?.accountManagerId ||
					orderDetailsInfo?.accountManagerResponse?.id ||
					0,
				brandId: brandId || orderStyles[0]?.brandId || orderDetailsInfo?.brandResponse?.id || null,
				dealId:
					basicInfoData?.selectedDeal?.id ||
					orderStyles[0]?.projectDeal?.id ||
					orderStyles[0]?.dealId ||
					orderDetailsInfo?.dealId ||
					null,
				subBrandId:
					basicInfoData?.selectedSubBrand?.id ||
					basicInfoData?.selectedSubBrand?.value ||
					orderDetailsInfo?.subBrandId ||
					null,
				incoterms:
					basicInfoData?.selectedIncoTerm?.id ||
					basicInfoData?.selectedIncoTerm?.value ||
					orderDetailsInfo?.incoterms ||
					'',
				incotermsLocation:
					basicInfoData?.projectDetails?.incotermsLocation ||
					orderDetailsInfo?.incotermsLocation ||
					'',
				revenueStream,
				shipVia: orderDetailsInfo?.shipVia || '',
				season: basicInfoData?.projectDetails?.season || orderDetailsInfo?.season || '',
				paymentTerms: orderDetailsInfo?.paymentTerms || '',
				poDocIdList: poDocIdList || [],
				designs: designs,
				extractionResultId: extractionResultId || null
			};
			const response = await setExtractPoDocuments({ requestBody }).unwrap();

			if (response?.success || response?.data) {
				toastSuccess(response?.message || 'Order placed successfully');
				setShow(false);
				setSelectedStyleItem(null);
				setOpenStyleModal(false);
				navigate('/orders/my-orders?active=PENDING');
			} else {
				toastError(response?.message || 'Failed to place order');
			}
		} catch (error) {
			const errorMessage = error?.data?.message || error?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			dispatch(closeLoader());
			setIsPlacingOrder(false);
		}
	};
	useEffect(() => {
		(async () => {
			try {
				await fetchEnumValues('info.nitex.app.product.SetPackStyleFlag', setSetPackStyleFlag);
				await fetchEnumValues('info.nitex.app.materials.Season', setSeasons);
			} catch {}
		})();
	}, []);

	useEffect(() => {
		if (openStyleModal) {
			buttonRef.current.click();
		}
	}, [openStyleModal]);

	const getOrderQuotes = async (orderId) => {
		setLoading(true);
		let url = 'getOrderQuotes';
		const search = window.location.search;
		const params = new URLSearchParams(search);
		const requestId = params.get('requestId');
		const routeFrom = params.get('routeFrom');
		const useCancelQuotes = requestId !== null && routeFrom === 'ORDER_CANCEL';
		if (useCancelQuotes) {
			url = `${BASE_URL}/order/cancel/request/${requestId}/quotes`;
		}

		await ApiClient.GET(url, useCancelQuotes ? '' : +orderId, useCancelQuotes)
			.then(({ data }) => {
				const mappedStyles = data.map((item) => {
					return {
						...item,
						referenceNumber: item?.productReferenceNumber || '',
						priceType: item?.priceType || orderDetailsInfo?.incoterms
					};
				});
				setOrderStyles(sortStylesForDisplay(mappedStyles));

				setLoading(false);
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const onSetEditIndex = (id, editable) => {
		setGroupData(
			(prevGroupPoData) =>
				prevGroupPoData?.map((group) => ({
					...group,
					poData:
						group?.poData?.map((poItem) => {
							if (!poItem) return poItem;
							if (poItem.id === id) {
								return { ...poItem, isEdit: editable };
							}
							return poItem && typeof poItem === 'object' && 'isEdit' in poItem
								? poItem
								: { ...poItem, isEdit: false };
						}) || []
				})) || []
		);

		const updatedArray = orderStyles.map((item) => {
			if (item.id === id) {
				const updatedItem = {
					...item,
					isEdit: editable
				};
				if (editable) {
					updatedItem._prevProgramTypeId = item.programTypeId ?? null;
					updatedItem._prevProgramType = item.programType ?? null;
					updatedItem._prevSalesMarginPercentage = item.salesMarginPercentage ?? null;
					updatedItem._prevSalesMarginFobAmount = item.salesMarginFobAmount ?? null;
				}
				return updatedItem;
			}
			return item;
		});
		setOrderStyles(updatedArray);
	};

	const onCancelEdit = (id) => {
		const updatedArray = orderStyles.map((item) => {
			if (item.id === id) {
				const {
					_prevProgramTypeId,
					_prevProgramType,
					_prevSalesMarginPercentage,
					_prevSalesMarginFobAmount,
					...rest
				} = item;
				return {
					...rest,
					isEdit: false,
					programTypeId: _prevProgramTypeId !== undefined ? _prevProgramTypeId : item.programTypeId,
					programType: _prevProgramType !== undefined ? _prevProgramType : item.programType,
					salesMarginPercentage:
						_prevSalesMarginPercentage !== undefined
							? _prevSalesMarginPercentage
							: item.salesMarginPercentage,
					salesMarginFobAmount:
						_prevSalesMarginFobAmount !== undefined
							? _prevSalesMarginFobAmount
							: item.salesMarginFobAmount
				};
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getOrderStylesGroupData(updatedArray));
	};

	const addEdt = async (rfqId) => {
		const splitDealIds = await askDealSplit(orderId);
		if (splitDealIds === null) return;

		try {
			dispatch(openLoader());
			const data = await ApiClient.POST('duplicateRfq', '', `${rfqId}${dealSplitQuery(splitDealIds)}`);
			if (data?.status === 200) {
				toastSuccess('Success!');
				getOrderQuotes(orderId);
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			dispatch(closeLoader());
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			dispatch(closeLoader());
		}
	};

	const duplicateEdtWithoutApi = (singlePo) => {
		if (!singlePo) return;

		const duplicateId = `local_dup_${singlePo?.id || Date.now()}_${Date.now()}`;
		const duplicatedColorWiseSizeQuantityPairList = (singlePo?.colorWiseSizeQuantityPairList || []).map(
			(colorItem) => ({
				...colorItem,
				compositeColorList: Array.isArray(colorItem?.compositeColorList)
					? colorItem.compositeColorList.map((compositeColor) => ({ ...compositeColor }))
					: colorItem?.compositeColorList,
				sizeQuantityPairList: (colorItem?.sizeQuantityPairList || []).map((sizeItem) => ({
					...sizeItem
				}))
			})
		);
		const duplicatedPo = {
			...singlePo,
			id: duplicateId,
			rfqId: duplicateId,
			isBaseRfq: false,
			isEdit: true,
			colorWiseSizeQuantityPairList: duplicatedColorWiseSizeQuantityPairList,
			sizeQuantityPairList: (singlePo?.sizeQuantityPairList || []).map((sizeItem) => ({
				...sizeItem
			})),
			productColorIds: [...(singlePo?.productColorIds || [])],
			sizeWisePoItemIdentifier:
				singlePo?.sizeWisePoItemIdentifier && typeof singlePo.sizeWisePoItemIdentifier === 'object'
					? { ...singlePo.sizeWisePoItemIdentifier }
					: singlePo?.sizeWisePoItemIdentifier,
			colorWisePoItemIdentifier:
				singlePo?.colorWisePoItemIdentifier && typeof singlePo.colorWisePoItemIdentifier === 'object'
					? { ...singlePo.colorWisePoItemIdentifier }
					: singlePo?.colorWisePoItemIdentifier,
			sizeWiseBuyerPrice:
				singlePo?.sizeWiseBuyerPrice && typeof singlePo.sizeWiseBuyerPrice === 'object'
					? { ...singlePo.sizeWiseBuyerPrice }
					: singlePo?.sizeWiseBuyerPrice,
			colorWiseBuyerPrice:
				singlePo?.colorWiseBuyerPrice && typeof singlePo.colorWiseBuyerPrice === 'object'
					? { ...singlePo.colorWiseBuyerPrice }
					: singlePo?.colorWiseBuyerPrice,
			documentResponseList: (singlePo?.documentResponseList || []).map((docItem) => ({
				...docItem
			})),
			poNumber: singlePo?.poNumber || '',
			poReceiveDate: singlePo?.poReceiveDate || '',
			prevDeliveryDate: singlePo?.deliveryDate || '',
			etdChangePending: false,
			deliveryDateChangeResponseList: []
		};

		const targetIndex = orderStyles.findIndex((item) => item.id === singlePo.id);
		const updatedStyles = [...orderStyles];

		if (targetIndex > -1) {
			updatedStyles.splice(targetIndex + 1, 0, duplicatedPo);
		} else {
			updatedStyles.push(duplicatedPo);
		}

		setOrderStyles(updatedStyles);
		setGroupData(getOrderStylesGroupData(updatedStyles));
		toastSuccess('Success!');
	};

	const onChangePO = (array, id, keyPath, value, index) => {
		const updatedArray = array.map((item, i) => {
			const isMatch = id != null ? item.id === id : i === index;
			if (isMatch) {
				const updatedItem = {
					...item,
					rfqId: id ?? item.id,
					[keyPath]: value
				};
				if (keyPath === 'deliveryDate' && !item?.prevDeliveryDate) {
					updatedItem['prevDeliveryDate'] = item.deliveryDate;
				}
				// Design No input reads referenceNumber first, so keep both keys in sync
				if (keyPath === 'productReferenceNumber') {
					updatedItem['referenceNumber'] = value;
				}
				return updatedItem;
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getOrderStylesGroupData(updatedArray));
		return updatedArray;
	};

	const createNewProgramType = async (name, setIsOpen) => {
		const trimmedName = String(name || '').trim();
		if (!trimmedName) return;

		try {
			setIsProgramTypeSubmitting(true);
			await createProgramType({ name: trimmedName }).unwrap();
			await fetchProgramTypes();
			toastSuccess('Program type created successfully');
			if (typeof setIsOpen === 'function') {
				setIsOpen(false);
			}
		} catch (error) {
			toastError(error?.data?.message || 'Failed to create program type.');
		} finally {
			setIsProgramTypeSubmitting(false);
		}
	};

	const onProgramTypeSelect = (singlePo, programType, index) => {
		const updatedArray = orderStyles.map((item, i) => {
			const isMatch = singlePo.id != null ? item.id === singlePo.id : i === index;
			if (isMatch) {
				return {
					...item,
					programTypeId: programType?.id || null,
					programType: programType
						? {
								id: programType.id,
								name: programType.name
							}
						: null
				};
			}
			return item;
		});

		setOrderStyles(updatedArray);
		setGroupData(getOrderStylesGroupData(updatedArray));
	};

	const renderProgramTypeOptions = () => {
		return programTypeOptions.map((programType) => ({
			...programType,
			value: programType.name
		}));
	};

	const formatProjectDealOptions = (dealList) => {
		if (!Array.isArray(dealList)) {
			return [];
		}
		return dealList.map((deal) => ({
			...deal,
			value: deal?.dealName
		}));
	};
	const handleDealChange = (singlePoId, selectedDeal, index) => {
		const updatedArray = orderStyles.map((item, i) => {
			const isMatch = singlePoId != null ? item.id === singlePoId : i === index;
			if (isMatch) {
				return {
					...item,
					projectDeal: selectedDeal
						? {
								id: selectedDeal.dealId,
								dealName: selectedDeal.dealName
							}
						: null
				};
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getOrderStylesGroupData(updatedArray));
	};

	const onProgramTypeEdit = (programType) => {
		setSelectedProgramType(programType);
		setProgramTypeName(programType?.name || '');
		setShowProgramTypeModal(true);
	};

	const closeProgramTypeModal = () => {
		setShowProgramTypeModal(false);
		setSelectedProgramType(null);
		setProgramTypeName('');
	};

	const onUpdateProgramType = async () => {
		const trimmedName = String(programTypeName || '').trim();
		if (!trimmedName || !selectedProgramType?.id) return;

		try {
			setIsProgramTypeSubmitting(true);
			await updateProgramType({ id: selectedProgramType.id, name: trimmedName }).unwrap();
			await fetchProgramTypes();
			setOrderStyles((prev) =>
				prev.map((item) =>
					item?.programTypeId === selectedProgramType.id
						? {
								...item,
								programType: {
									...(item?.programType || {}),
									id: selectedProgramType.id,
									name: trimmedName
								}
							}
						: item
				)
			);
			toastSuccess('Program type updated successfully');
			closeProgramTypeModal();
		} catch (error) {
			toastError(error?.data?.message || 'Failed to update program type.');
		} finally {
			setIsProgramTypeSubmitting(false);
		}
	};

	const getBoolVal = async (finalPayload) => {
		let isValid = true;

		if (!finalPayload || !Array.isArray(finalPayload)) {
			return false;
		}

		finalPayload.forEach((item) => {
			if (!isValid) return;

			// Safely check for colorWiseSizeQuantityPairList
			const colorList = item?.colorWiseSizeQuantityPairList;
			if (!colorList || !Array.isArray(colorList)) {
				isValid = false;
				return;
			}

			if (item.buyerQuotationType === 'COLORWISE') {
				const buyerPrice = item?.colorWiseBuyerPrice || {};
				if (
					!buyerPrice ||
					typeof buyerPrice !== 'object' ||
					Object.keys(buyerPrice).length !== colorList.length
				) {
					isValid = false;
				}
			} else if (item.buyerQuotationType === 'SIZEWISE') {
				const currentSizes = colorList?.[0]?.sizeQuantityPairList;
				const sizePrice = item?.sizeWiseBuyerPrice || {};

				if (
					!sizePrice ||
					typeof sizePrice !== 'object' ||
					!colorList ||
					!currentSizes ||
					currentSizes.length === 0
				) {
					isValid = false;
				} else {
					const allSizesHavePrice = currentSizes.every((size) => {
						const price = sizePrice?.[size?.id];
						return price !== null && price !== undefined && price !== '';
					});
					if (!allSizesHavePrice) {
						isValid = false;
					}
				}
			} else if (item.buyerQuotationType === 'DESIGNWISE') {
				if (!item?.designWiseBuyerPrice) {
					isValid = false;
				}
			} else {
				isValid = false;
			}
		});

		return isValid;
	};

	const isPoNumberExist = (productId, rfqId) => {
		let selectedProduct = orderStyles.find((item) => item.id === rfqId);
		if (selectedProduct && !selectedProduct?.poNumber) {
			let errorObj = {
				[`po_${rfqId}`]: 'Po number is required'
			};
			setErrors(errorObj);
			return false;
		}
		setErrors({});
		return true;
	};

	const updateInfo = async (productId, rfqId, etdLogInfo = {}, skipOrderValidation = false) => {
		if (!skipOrderValidation && !isPoNumberExist(productId, rfqId)) return;

		const finalPayload = orderStyles
			.filter((order) => order.productId === productId && order.id === rfqId)
			.map((order) => {
				const hasFobAmount =
					order.salesMarginFobAmount !== null &&
					order.salesMarginFobAmount !== undefined &&
					order.salesMarginFobAmount !== '';
				const hasPercentage =
					order.salesMarginPercentage !== null &&
					order.salesMarginPercentage !== undefined &&
					order.salesMarginPercentage !== '';
				const salesMarginFields = hasFobAmount
					? { salesMarginFobAmount: order.salesMarginFobAmount, salesMarginPercentage: undefined }
					: hasPercentage
						? {
								salesMarginPercentage: order.salesMarginPercentage,
								salesMarginFobAmount: undefined
							}
						: {};

				const orderWithoutDeal = { ...order };
				delete orderWithoutDeal.dealName;
				delete orderWithoutDeal.projectDeal;
				return {
					rfqId: order.id,
					...orderWithoutDeal,
					...salesMarginFields,
					referenceNumber: order.productReferenceNumber,
					dealId: order.projectDeal?.id || order.dealId,
					...etdLogInfo,
					...(isDrafted && { deliveryDateChangeRequestorType: 'INITIAL_ETD' })
				};
			});

		const hasNonZeroQuantity = finalPayload?.some((item) => {
			return item.colorWiseSizeQuantityPairList?.some((color) => {
				return color.sizeQuantityPairList?.some((size) => size.quantity > 0);
			});
		});

		const hasNonUnitPrice = await getBoolVal(finalPayload);

		if (hasNonZeroQuantity || hasNonUnitPrice) {
			setErrorDesign({});
		}
		if (!skipOrderValidation && !hasNonZeroQuantity) {
			setErrorDesign({
				totalError: 'Atleast one quantity is required',
				designId: rfqId
			});
			return;
		}
		if (!hasNonUnitPrice) {
			setErrorDesign({
				totalError: 'Unit price is required',
				designId: rfqId
			});
			return;
		}
		const currentStyle = orderStyles.find((order) => order.id === rfqId);
		if (!skipOrderValidation && type === 'PENDING') {
			if (!currentStyle?.programTypeId && !currentStyle?.programType?.id) {
				setProgramTypeErrors((prev) => ({
					...prev,
					[rfqId]: 'Program type is mandatory'
				}));
				return;
			}
			if (
				(currentStyle?.salesMarginPercentage === null ||
					currentStyle?.salesMarginPercentage === undefined ||
					currentStyle?.salesMarginPercentage === '') &&
				(currentStyle?.salesMarginFobAmount === null ||
					currentStyle?.salesMarginFobAmount === undefined ||
					currentStyle?.salesMarginFobAmount === '')
			) {
				setSalesMarginErrors((prev) => ({
					...prev,
					[rfqId]: 'Sales margin is mandatory'
				}));
				return;
			}
		}

		// a line that takes another deal puts the order on that deal, so the deal may need splitting
		const productsWithNewDeal = orderStyles
			.filter((order) => order.productId === productId && order.id === rfqId)
			.filter((order) => order.projectDeal?.id && order.projectDeal.id !== order.dealId)
			.map((order) => ({ dealId: order.projectDeal.id, productId: order.productId }));
		const splitDealIds =
			productsWithNewDeal.length > 0
				? await askDealSplit(orderId, { productsToAdd: productsWithNewDeal })
				: [];
		if (splitDealIds === null) return;

		try {
			dispatch(openLoader());
			const { data } = await ApiClient.PUT('updateRfqInfo', finalPayload, dealSplitQuery(splitDealIds));

			// Handle successful response
			if (data?.success) {
				toastSuccess(data.message);
				getOrderQuotes(orderId);
				onSetEditIndex(rfqId, false);
				setError('');
				setProgramTypeErrors((prev) => {
					const updated = { ...prev };
					delete updated[rfqId];
					return updated;
				});
				setSalesMarginErrors((prev) => {
					const updated = { ...prev };
					delete updated[rfqId];
					return updated;
				});
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			dispatch(closeLoader());
			// Handle errors gracefully
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			dispatch(closeLoader());
		}
	};

	const deleteRfq = async (item) => {
		let ids = '';
		if (item?.isBaseRfq) {
			const matchingIds = orderStyles
				.filter((entry) => entry.productId === item.productId)
				.map((entry) => entry.id);
			ids = matchingIds.join(',');
		} else {
			ids += item?.id;
		}
		try {
			dispatch(openLoader());
			let url = 'deleteRfq';
			let params = `?ids=${ids}`;
			if (!isDrafted && !item.isBaseRfq) {
				url = 'deleteDuplicateRfq';
				params = `/${ids}`;
			}
			const { data } = await ApiClient.DELETE(url, '', params);
			if (data?.success) {
				toastSuccess(data.message);
				getOrderQuotes(orderId);
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			dispatch(closeLoader());
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			dispatch(closeLoader());
		}
	};
	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};
	const openDesignViewModal = (item) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack:
					item?.isSet || item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: item?.productId,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST',
				designIdList: designIdList
			})
		);
	};
	const handleRFQIds = (rfqId) => {
		if (selectedRFQIds.includes(rfqId)) {
			let tmpList = selectedRFQIds.filter((item) => item !== rfqId);
			setSelectedRFQIds(tmpList);
		} else {
			setSelectedRFQIds((prev) => [...prev, rfqId]);
		}
	};
	const filterStylesData = () => {
		if (hideStatusType) {
			return orderStyles.filter((item) => {
				switch (hideStatusType) {
					case 'piCreated':
						return item.isPiCreated === true;
					case 'piPending':
						return item.isPiCreated === false;
					case 'scCreated':
						return item.isScCreated === true;
					case 'scPending':
						return item.isScCreated === false;
					case 'tnaCreated':
						return item.isTNACreated === true;
					case 'tnaPending':
						return item.isTNACreated === false;
					default:
						return true;
				}
			});
		} else {
			return orderStyles;
		}
	};
	const isDisabledSubmitButton = (array) => {
		return array.some((item) => item.draftStatus !== 'COMPLETED' || item.isEdit);
	};
	const shouldShowSplitTag = (singlePo, poData = []) => {
		if (!singlePo) return false;
		if (singlePo?.isBaseRfq === false) return true;
		const firstSameProduct = poData.find((po) => po?.productId === singlePo?.productId);
		return !!firstSameProduct && firstSameProduct?.id !== singlePo?.id;
	};
	const handleClose = () => {
		setShow(false);
	};

	const handleAddNewStyle = (newStyles) => {
		let preparedStyles = newStyles;

		if (Array.isArray(newStyles)) {
			preparedStyles = newStyles.map((style) => {
				return {
					...style,
					...(isFromPoAutomation
						? {
								isEdit: true,
								poReceiveDate:
									style?.poReceiveDate || basicInfoData?.projectDetails?.startDate || '',
								deliveryDate:
									style?.deliveryDate || basicInfoData?.projectDetails?.deliveryDate || '',
								projectDeal: style?.projectDeal || basicInfoData?.selectedDeal || null
							}
						: {})
				};
			});
		}

		if (typeof addNewStyle === 'function') {
			addNewStyle(preparedStyles);
			return;
		}

		if (Array.isArray(newStyles) && newStyles.length > 0) {
			setOrderStyles((prevStyles) => [...prevStyles, ...preparedStyles]);
		}
	};
	const handleAddNewStyleForPoAutomation = (newStyles) => {
		let preparedStyles = newStyles;
		if (Array.isArray(newStyles)) {
			preparedStyles = newStyles.map((style) => ({
				...style,
				isEdit: true,
				poReceiveDate: style?.poReceiveDate || basicInfoData?.projectDetails?.startDate || '',
				deliveryDate: style?.deliveryDate || basicInfoData?.projectDetails?.deliveryDate || '',
				projectDeal: style?.projectDeal || basicInfoData?.selectedDeal || null,
				colorWiseSizeQuantityPairList: [],
				sizeQuantityPairList: [],
				productColorIds: [],
				sizeWisePoItemIdentifier: {},
				colorWisePoItemIdentifier: {},
				designWisePoItemIdentifier: null,
				sizeWiseBuyerPrice: {},
				colorWiseBuyerPrice: {},
				designWiseBuyerPrice: null
			}));
		}
		if (typeof addNewStyle === 'function') {
			addNewStyle(preparedStyles);
			return;
		}

		if (Array.isArray(newStyles) && newStyles.length > 0) {
			setOrderStyles((prevStyles) => [...prevStyles, ...preparedStyles]);
		}
	};

	if (!groupData) {
		// Show a loading spinner or placeholder while data is being initialized
		return <div>Loading...</div>;
	}

	const renderRightSidePanel = () => {
		return (
			<DesignsByCollection
				addNewStyle={isFromPoAutomation ? handleAddNewStyleForPoAutomation : handleAddNewStyle}
				stylesData={productIds}
				setOpenStyleModal={setOpenStyleModal}
				collectionId={collectionId || orderDetailsInfo.collectionId}
				folderId={orderDetailsInfo.folderId || folderId}
				requestFrom={'ORDER'}
			/>
		);
	};

	const removeSingePo = (singlePo) => {
		const updatedStyles = orderStyles.filter((item) => item.id !== singlePo.id);
		setOrderStyles(updatedStyles);
		setGroupData(getOrderStylesGroupData(updatedStyles));
	};

	const submitETDLog = async (postData) => {
		await updateInfo(selectedPo.productId, selectedPo.id, postData, true);
		setSelectedPo(null);
	};

	// Two rows can share the same productId and poNumber (split), so match on the row id first
	const isSameStyleRow = (style, target) =>
		target?.id != null
			? style.id === target.id
			: style.productId === target?.productId && style.poNumber === target?.poNumber;

	const handleCollectionItemSelect = (item, targetPo) => {
		const targetPoNumber = targetPo?.poNumber;
		const targetProductId = targetPo?.productId;
		const indexToUpdate = orderStyles.findIndex((style) => isSameStyleRow(style, targetPo));
		if (indexToUpdate === -1) return;

		const updatedStyles = [...orderStyles];
		updatedStyles[indexToUpdate] = {
			...updatedStyles[indexToUpdate],
			productId: item.id,
			referenceNumber: item.referenceNumber,
			docUrl: item.designDocuments?.[0]?.docUrl || '',
			documentResponseList: item.designDocuments || [],
			productName: item.categoryResponse?.name || '',
			categoryResponse: item.categoryResponse || null
		};
		setOrderStyles(updatedStyles);
		setGroupData(getOrderStylesGroupData(updatedStyles));
		setSelectedStyleItem({
			...item,
			updatedPoNumber: targetPoNumber,
			updatedProductId: targetProductId,
			updatedStyleId: targetPo?.id
		});
	};

	const renderCollectionDropDown = (singlePoData) => {
		// Favourite folder items wrap the product inside productResponse
		const sourceItems = collectionData?.data?.length
			? collectionData.data
			: (folderCollectionData?.data || []).map((item) => item?.productResponse || item);
		const items = (sourceItems || [])
			.filter(
				(item) =>
					(item.fabricName && item.fabricDetails) ||
					item?.isSet ||
					item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
			)
			.map((item) => ({
				...item,
				name: item.referenceNumber,
				value: item.referenceNumber,
				docUrl: item.designDocuments?.[0]?.docUrl || '',
				productSubCategoryName: item.productSubCategory?.name || '',
				productName: item.productName || ''
			}));
		const currentStyle = orderStyles.find((s) => isSameStyleRow(s, singlePoData));
		return (
			<CustomDropdown
				title="Select Item"
				items={items}
				onItemClick={(selectedItem) => handleCollectionItemSelect(selectedItem, singlePoData)}
				toolTipLength={12}
				selectedItem={currentStyle?.referenceNumber || ''}
				selectedImageClass="selected-image"
				isSearchable={true}
				customDesign={(item) => (
					<div className="d-flex align-items-center gap-2">
						{item.docUrl && <img src={item.docUrl} alt={item.value} />}
						<span>{item.value}</span>
					</div>
				)}
			/>
		);
	};
	const getSelectedItem = (singlePo) =>
		selectedStyleItem?.id === singlePo?.productId &&
		selectedStyleItem?.updatedPoNumber === singlePo?.poNumber
			? selectedStyleItem
			: singlePo;
	const getSetItem = (singlePo) => {
		const selectedItem = getSelectedItem(singlePo);
		return selectedItem?.isSet || selectedItem?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
			? selectedItem
			: null;
	};
	const renderSetItemImages = (singlePo) => {
		const setItem = getSetItem(singlePo);
		return renderSetImages(setItem?.designDocuments || setItem?.documentResponseList, '100%');
	};

	return (
		<>
			{!isDrafted && (
				<div className="info-and-button d-flex justify-content-between align-items-center pt-4 pb-4">
					<p className="medium-14 gray_dark_01">
						Designs info{' '}
						<span className="regular-14 gray_dark_02 ml-2">
							(Total no of style - {orderStyles?.length || 0})
						</span>
					</p>
					<div>
						<button
							className="button outline size32"
							onClick={() => setOpenOrderDesignModal(true)}
						>
							<span className="ml-1">{stylesCount} Design</span>
						</button>
						<button
							className="button outline size32 ml-2"
							data-toggle="modal"
							data-target="#add-multiple-design"
							ref={buttonRef}
							onClick={() => setOpenStyleModal(true)}
						>
							<CommonImage src="/icons/plus-20.svg" alt="" />
							<span className="ml-1">Style</span>
						</button>
					</div>
				</div>
			)}

			{orderCancelInfo && Object.keys(orderCancelInfo).length > 0 && (
				<OrderCancelInfo orderCancelInfo={orderCancelInfo} getOrderCancelInfo={getOrderCancelInfo} />
			)}

			<>
				<div className="accordion">
					{groupData?.map((item, index) => (
						<div
							className={`card ${isOverflow ? 'overflow-visible' : ''} ${index === groupData.length - 1 ? 'last-card' : ''} ${!isDisabledSubmitButton(item?.poData) ? 'active' : ''}`}
							key={`card_${index}`}
						>
							<div className={`collapse show`}>
								{item?.poData.map((singlePo, index) => (
									<div
										key={singlePo.id || index}
										className={`card-body border-0 designs-info-section radius-6 ${singlePo.draftStatus === 'COMPLETED' && isDisabledSubmitButton(item?.poData) ? 'active' : ''} ${
											orderCancelInfo?.requestType === 'PRODUCT' &&
											orderCancelInfo?.status === 'REQUESTED' &&
											singlePo.canceledStatus === 'REQUESTED'
												? 'highlight-row'
												: ''
										} ${
											hasEtdApprovalHighlight && !rfqIdWiseEtdApproval[singlePo.id]
												? 'etd-approval-dimmed'
												: ''
										}`}
									>
										<div className="designs-row order-desing-row">
											<div className={`single-design p-0 m-0 border-0`}>
												<div className="single-design-card with-editable">
													<div className="card-top">
														<div className="edit-items">
															<div className="select-and-edit d-flex justify-content-between">
																{!isDrafted &&
																	(singlePo?.status === 'ORDER_PLACED' ||
																		singlePo?.status === 'PENDING' ||
																		singlePo?.status === 'PRODUCT_SOLD' ||
																		singlePo?.status === 'RUNNING') && (
																		<div className="custom-chekbox">
																			<div className="form-group">
																				<input
																					type="checkbox"
																					id={`stage_${singlePo.id}`}
																					checked={selectedRFQIds.includes(
																						singlePo.id
																					)}
																					onChange={() =>
																						handleRFQIds(
																							singlePo.id
																						)
																					}
																				/>
																				<label
																					htmlFor={`stage_${singlePo.id}`}
																				></label>
																			</div>
																		</div>
																	)}
															</div>
														</div>
														{isMissingProductId && (
															<div className="missing-style position-absolute top-0 right-0">
																{renderCollectionDropDown(singlePo)}
															</div>
														)}
														<>
															{getSetItem(singlePo) ? (
																<div
																	onClick={() =>
																		openDesignViewModal(singlePo)
																	}
																>
																	{renderSetItemImages(singlePo)}
																</div>
															) : (
																<CommonImage
																	src={
																		selectedStyleItem?.updatedStyleId ===
																			singlePo.id &&
																		selectedStyleItem?.id ===
																			singlePo.productId
																			? selectedStyleItem
																					?.designDocuments?.[0]
																					?.docUrl
																			: singlePo?.docUrl ||
																				singlePo
																					?.documentResponseList?.[0]
																					?.docUrl
																	}
																	isStatic={false}
																	onClick={() =>
																		openDesignViewModal(singlePo)
																	}
																/>
															)}
															<div className="order-status d-flex justify-content-between">
																{getSelectedItem(singlePo)?.isSet && setTag()}
																{getSelectedItem(singlePo)?.isPack &&
																	packTag()}
																{shouldShowSplitTag(singlePo, item?.poData) &&
																	splitTag()}
																{/* A style added once the order is in production
																    has no TNA of its own yet. */}
																{orderDetailsInfo?.status === 'RUNNING' &&
																	!singlePo?.isTNACreated &&
																	createTnaTag()}
															</div>
														</>
													</div>
												</div>
												<div className="title-and-form mb-5">
													<div
														className={`d-flex justify-content-between align-items-center`}
													>
														<div className="d-flex align-items-center">
															<p className="medium-16 gray_dark_01 mr-3">
																{singlePo.isBaseRfq && (
																	<>
																		{singlePo.referenceNumber ||
																			item.productReferenceNumber}{' '}
																		<span className="regular-12 gray_dark_02">
																			(
																			{singlePo?.categoryResponse
																				?.name ||
																				singlePo?.productSubCategoryName ||
																				item?.name ||
																				''}
																			)
																		</span>
																	</>
																)}
															</p>
															<div className="pending-order-stepper on-design-info-item">
																{!isDrafted && (
																	<ul className="d-flex">
																		<li
																			className={
																				singlePo.isPiCreated
																					? ''
																					: 'disabled'
																			}
																		>
																			<span className="step-count done">
																				<CommonImage
																					src="/icons/tick-green.png"
																					alt="tich"
																				/>
																			</span>
																			PI
																		</li>
																		<li
																			className={
																				singlePo.isScCreated
																					? ''
																					: 'disabled'
																			}
																		>
																			<span className="step-count done">
																				<CommonImage
																					src="/icons/tick-green.png"
																					alt="tich"
																				/>
																			</span>
																			Sales contact
																		</li>
																		<li
																			className={
																				singlePo.isTNACreated
																					? ''
																					: 'disabled'
																			}
																		>
																			<span className="step-count done">
																				<CommonImage
																					src="/icons/tick-green.png"
																					alt="tich"
																				/>
																			</span>
																			TNA
																		</li>
																	</ul>
																)}
															</div>
														</div>
														{poSubmitButton && (
															<div className="edit-and-update-btn gap-4 d-flex justify-content-center">
																<span
																	className="delete-btn cursor-pointer"
																	onClick={() => removeSingePo(singlePo)}
																>
																	<CommonImage
																		src="/icons/delete24.svg"
																		width={20}
																		height={20}
																		alt="trash"
																	/>
																</span>
																<span
																	className="duplicate-btn cursor-pointer"
																	onClick={() =>
																		duplicateEdtWithoutApi(singlePo)
																	}
																>
																	<CommonImage
																		src="/icons/copy-gray.svg"
																		width={20}
																		height={20}
																		alt="trash"
																	/>
																</span>
															</div>
														)}
														{!poSubmitButton && (
															<div className="edit-and-update-btn  d-flex justify-content-center">
																{singlePo.isEdit && (
																	<>
																		<button
																			type="button"
																			className="button text size32 mr-2 px-0"
																			onClick={() =>
																				onCancelEdit(singlePo.id)
																			}
																		>
																			Cancel
																		</button>
																		<button
																			type="button"
																			className="button text size32 mr-2 px-0"
																			onClick={() => {
																				if (
																					!isDrafted &&
																					singlePo?.prevDeliveryDate &&
																					singlePo.deliveryDate !==
																						singlePo.prevDeliveryDate
																				) {
																					setSelectedPo({
																						productId:
																							singlePo.productId,
																						id: singlePo.id
																					});
																				} else {
																					updateInfo(
																						singlePo.productId,
																						singlePo.id
																					);
																				}
																			}}
																		>
																			<span className="blue">
																				Update
																			</span>
																		</button>
																	</>
																)}
																<div className="option">
																	<div className="dropdown">
																		<button
																			className="btn btn-default dropdown-toggle"
																			type="button"
																			id="menu1"
																			data-toggle="dropdown"
																			aria-expanded="false"
																		>
																			<CommonImage
																				src="/icons/more_vertical.svg"
																				alt=""
																			/>
																		</button>
																		<ul
																			className="dropdown-menu dropdown-menu-right shadow-lg"
																			role="menu"
																			aria-labelledby="menu1"
																			x-placement="bottom-end"
																		>
																			<li
																				role="presentation"
																				className="px-4 pb-3 pt-3"
																			>
																				<a
																					role="menuitem"
																					tabindex="-1"
																					className="font-weight-normal text-black"
																					//href="/invoices/edit/10052"
																					onClick={() =>
																						onSetEditIndex(
																							singlePo.id,
																							true
																						)
																					}
																				>
																					Edit
																				</a>
																			</li>
																			<li
																				role="presentation"
																				className="px-4 pb-3 pt-3"
																			>
																				<a
																					role="menuitem"
																					tabindex="-1"
																					className="font-weight-normal  text-black"
																					onClick={() =>
																						addEdt(singlePo?.id)
																					}
																				>
																					Add ETD/PO
																				</a>
																			</li>
																			{(isDrafted ||
																				(!isDrafted &&
																					!singlePo.isBaseRfq)) && (
																				<li
																					role="presentation"
																					className="px-4 pb-3"
																				>
																					<a
																						role="menuitem"
																						tabindex="-1"
																						className="font-weight-normal  text-black"
																						onClick={() =>
																							deleteRfq(
																								singlePo
																							)
																						}
																					>
																						Delete
																					</a>
																				</li>
																			)}
																		</ul>
																	</div>
																</div>
															</div>
														)}
													</div>
													{/* {!singlePo.isBaseRfq && singlePo.isEdit && (
														<div className="split-order-form d-block">
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-4">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				PO No
																			</label>
																			<input
																				type="text"
																				id=""
																				placeholder="PO No"
																				value={singlePo.poNumber}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poNumber',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																			{errors?.[
																				`po_${singlePo.id}`
																			] && (
																				<p className="error">
																					{
																						errors?.[
																							`po_${singlePo.id}`
																						]
																					}
																				</p>
																			)}
																		</div>
																	</div>
																	<div className="col-md-4 ">
																		<div className="form-group mb-2">
																			<label htmlFor="design-no">
																				PO received date
																			</label>
																			<input
																				type="date"
																				placeholder="02/05/2024"
																				id=""
																				value={singlePo.poReceiveDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poReceiveDate',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																	<div className="col-md-4">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				ETD (dd/mm/yyyy)
																			</label>
																			<input
																				type="date"
																				id=""
																				placeholder="02/05/2024"
																				value={singlePo.deliveryDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'deliveryDate',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													)} */}
													{!singlePo.isEdit && (
														<div className="desing-all-content d-flex">
															<div className="design-details-info pt-3 ml-0">
																<div className="single-design-view">
																	<div className="content">
																		<p className="regular-14 gray_dark_02 mb-12">
																			PO NO:{' '}
																			<span className="gray_dark_01">
																				{' '}
																				{singlePo.poNumber}
																			</span>
																		</p>
																		<p className="regular-14 gray_dark_02 mb-12">
																			PO receive date:{' '}
																			<span className="gray_dark_01">
																				{singlePo.poReceiveDate
																					? moment(
																							singlePo.poReceiveDate
																						).format(
																							'DD-MMM-YYYY'
																						)
																					: ''}
																			</span>
																		</p>

																		{singlePo?.unitOfMeasurementQty && (
																			<p className="regular-14 gray_dark_02 mb-12">
																				{singlePo?.unitOfMeasurementType ===
																				'SET_STYLE'
																					? 'SET'
																					: singlePo?.unitOfMeasurementType ===
																						  'PACK_STYLE'
																						? 'PACK'
																						: 'Set/Pack'}
																				:{' '}
																				<span className="gray_dark_01">
																					{
																						singlePo?.unitOfMeasurementQty
																					}{' '}
																					Pcs
																				</span>
																			</p>
																		)}
																	</div>
																</div>
															</div>
															<div className="design-details-info pt-3">
																<div className="single-design-view">
																	<div className="content">
																		<p className="regular-14 gray_dark_02 mb-12">
																			Supplier:
																			<span className="gray_dark_01">
																				{' '}
																				{singlePo?.supplierName}
																			</span>
																		</p>
																		<div className="buyer-etd-and-timeline d-flex align-items-center">
																			<p className="regular-14 gray_dark_02 mb-12">
																				Buyer ETD:{' '}
																				<span className="gray_dark_01">
																					{moment(
																						singlePo.deliveryDate
																					).format('DD-MMM-YYYY')}
																				</span>
																			</p>
																			{singlePo.etdChangePending && (
																				<span className="etd-change-pending regular-12 ml-2">
																					ETD change pending
																				</span>
																			)}
																			{!isDrafted &&
																				singlePo
																					?.deliveryDateChangeResponseList
																					?.length > 0 && (
																					<div className="buyer-timeling position-relative">
																						<p
																							className="edited
																					regular-12 gray_dark_02
																					ml-2 cursor-pointer"
																						>
																							Edited
																						</p>
																						<EtdUpdateTimeline
																							timelineData={
																								singlePo.deliveryDateChangeResponseList
																							}
																						/>
																					</div>
																				)}
																		</div>
																		<p className="regular-14 gray_dark_02">
																			Deal:{' '}
																			<span className="gray_dark_01">
																				{singlePo?.dealName}
																			</span>
																		</p>
																	</div>
																</div>
															</div>
															<div className="design-details-info pt-3">
																<div className="single-design-view">
																	<div className="content">
																		<p className="regular-14 gray_dark_02 mb-12">
																			PO Qty:{' '}
																			<span className="gray_dark_01">
																				{singlePo?.total ||
																					singlePo?.quantity}
																			</span>
																		</p>

																		<p className="regular-14 gray_dark_02">
																			PO Amount:{' '}
																			<span className="gray_dark_01">
																				${singlePo?.amount}
																			</span>
																		</p>
																	</div>
																</div>
															</div>
															<div className="design-details-info">
																<div className="single-design-view">
																	<div className="content pt-3">
																		<p className="regular-14 gray_dark_02 mb-12">
																			Sales Margin (on FOB):{' '}
																			<span className="gray_dark_01">
																				{singlePo?.salesMarginPercentage !==
																					null &&
																				singlePo?.salesMarginPercentage !==
																					undefined &&
																				singlePo?.salesMarginPercentage !==
																					''
																					? `${singlePo.salesMarginPercentage}%`
																					: singlePo?.salesMarginFobAmount !==
																								null &&
																						  singlePo?.salesMarginFobAmount !==
																								undefined &&
																						  singlePo?.salesMarginFobAmount !==
																								''
																						? `$${singlePo.salesMarginFobAmount}`
																						: ''}
																			</span>
																		</p>
																		{salesMarginErrors?.[singlePo.id] && (
																			<p className="error regular-12">
																				{
																					salesMarginErrors[
																						singlePo.id
																					]
																				}
																			</p>
																		)}

																		<p className="regular-14 gray_dark_02">
																			Program Type:{' '}
																			<span className="gray_dark_01">
																				{singlePo?.programType?.name}
																			</span>
																		</p>
																		{programTypeErrors?.[singlePo.id] && (
																			<p className="error regular-12">
																				{
																					programTypeErrors[
																						singlePo.id
																					]
																				}
																			</p>
																		)}
																	</div>
																</div>
															</div>
														</div>
													)}
													{/* {singlePo.isEdit ? ( */}
													{singlePo.isEdit && (
														<div className="split-order-form pt-2">
															{/* Row 1: Design No, PO No, PO received date, ETD */}
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="design-no">
																				Design No
																			</label>
																			<input
																				type="text"
																				placeholder="Design No"
																				name="productReferenceNumber"
																				id={`productReferenceNumber_${singlePo.id}`}
																				value={
																					singlePo.referenceNumber ||
																					singlePo.productReferenceNumber
																				}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'productReferenceNumber',
																						e.target.value,
																						index
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				PO No*
																			</label>
																			<input
																				type="text"
																				id=""
																				placeholder="PO No"
																				value={singlePo.poNumber}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poNumber',
																						e.target.value,
																						index
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																			{errors?.[
																				`po_${singlePo.id}`
																			] && (
																				<p className="error">
																					{
																						errors?.[
																							`po_${singlePo.id}`
																						]
																					}
																				</p>
																			)}
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="po-receive-date">
																				PO received date
																			</label>
																			<input
																				type="date"
																				placeholder="02/05/2024"
																				id=""
																				value={singlePo.poReceiveDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poReceiveDate',
																						e.target.value,
																						index
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="etd">
																				ETD (mm/dd/yyyy)
																			</label>
																			<input
																				type="date"
																				id=""
																				placeholder="02/05/2024"
																				value={singlePo.deliveryDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'deliveryDate',
																						e.target.value,
																						index
																					)
																				}
																				disabled={
																					!singlePo.isEdit ||
																					singlePo.etdChangePending
																				}
																			/>
																			{singlePo.etdChangePending && (
																				<Tooltip
																					arrow
																					placement="top"
																					title={
																						<div className="etd-pending-tooltip">
																							<p className="mb-1">
																								Requested by:{' '}
																								{singlePo.pendingEtdRequestedBy ||
																									'-'}
																							</p>
																							<p className="mb-0">
																								New ETD:{' '}
																								{singlePo.pendingEtdNewDate
																									? moment(
																											singlePo.pendingEtdNewDate
																										).format(
																											'MM/DD/YYYY'
																										)
																									: '-'}
																							</p>
																						</div>
																					}
																				>
																					<span className="etd-pending-text regular-12 gray_dark_02">
																						ETD change request
																						pending
																					</span>
																				</Tooltip>
																			)}
																		</div>
																	</div>
																</div>
															</div>
															{/* Row 2: Sales Margin, Program Type, UOM */}
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label>
																				Sales Margin (on FOB)*
																			</label>
																			<div className="d-flex sales-margin-inputs">
																				<div className="sales-margin-input-wrapper">
																					<span className="sales-margin-prefix">
																						%
																					</span>
																					<input
																						type="number"
																						id={`salesMarginPercentage_${singlePo.id}`}
																						placeholder="Percentage"
																						value={
																							singlePo.salesMarginPercentage ??
																							''
																						}
																						onKeyPress={
																							validateFloatNumber
																						}
																						className="percentage-value"
																						onChange={(e) =>
																							onChangePO(
																								orderStyles,
																								singlePo.id,
																								'salesMarginPercentage',
																								e.target
																									.value ===
																									''
																									? ''
																									: parseFloat(
																											e
																												.target
																												.value
																										),
																								index
																							)
																						}
																						disabled={
																							!singlePo.isEdit ||
																							(singlePo.salesMarginFobAmount !==
																								null &&
																								singlePo.salesMarginFobAmount !==
																									undefined &&
																								singlePo.salesMarginFobAmount !==
																									'')
																						}
																					/>
																				</div>
																				<span className="gray_dark_02 regular-14 pt-2">
																					{' '}
																					or{' '}
																				</span>
																				<div className="sales-margin-input-wrapper">
																					<span className="sales-margin-prefix">
																						$
																					</span>
																					<input
																						type="number"
																						id={`salesMarginFobAmount_${singlePo.id}`}
																						placeholder="Amount"
																						value={
																							singlePo.salesMarginFobAmount ??
																							''
																						}
																						onKeyPress={
																							validateFloatNumber
																						}
																						onChange={(e) =>
																							onChangePO(
																								orderStyles,
																								singlePo.id,
																								'salesMarginFobAmount',

																								e.target
																									.value ===
																									''
																									? ''
																									: parseFloat(
																											e
																												.target
																												.value
																										),
																								index
																							)
																						}
																						disabled={
																							!singlePo.isEdit ||
																							(singlePo.salesMarginPercentage !==
																								null &&
																								singlePo.salesMarginPercentage !==
																									undefined &&
																								singlePo.salesMarginPercentage !==
																									'')
																						}
																					/>
																				</div>
																			</div>
																			{salesMarginErrors?.[
																				singlePo.id
																			] && (
																				<p className="error">
																					{
																						salesMarginErrors[
																							singlePo.id
																						]
																					}
																				</p>
																			)}
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group composition-item mb-0">
																			<label>Program Type*</label>
																			<div className="d-flex composition-and-qty">
																				<div className="w-100">
																					<CustomDropdown
																						title="Select program type"
																						isShowLabel={true}
																						isShowSearch={true}
																						isDisabled={
																							!singlePo.isEdit
																						}
																						items={renderProgramTypeOptions()}
																						onAddNewButtonClick={
																							createNewProgramType
																						}
																						showAddNewButton={isAdmin()}
																						selectedItem={
																							(
																								singlePo?.programType ||
																								programTypeOptions.find(
																									(item) =>
																										item.id ===
																										singlePo?.programTypeId
																								) ||
																								{}
																							).name || ''
																						}
																						isEditable={isAdmin()}
																						onEdit={
																							onProgramTypeEdit
																						}
																						onItemClick={(
																							value
																						) =>
																							onProgramTypeSelect(
																								singlePo,
																								value,
																								index
																							)
																						}
																					/>
																					{programTypeErrors?.[
																						singlePo.id
																					] && (
																						<p className="error regular-12 mt-1">
																							{
																								programTypeErrors[
																									singlePo
																										.id
																								]
																							}
																						</p>
																					)}
																				</div>
																			</div>
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group composition-item mb-0">
																			<label>UOM</label>
																			<div className="d-flex composition-and-qty">
																				<input
																					type="text"
																					className="input-percentage"
																					placeholder={
																						singlePo?.isPack
																							? 'Pack'
																							: singlePo?.isSet
																								? 'Set'
																								: 'Set/Pack'
																					}
																					onChange={(e) =>
																						onChangePO(
																							orderStyles,
																							singlePo.id,
																							'unitOfMeasurementType',
																							e.target.value,
																							index
																						)
																					}
																					value={
																						singlePo?.isPack
																							? 'Pack'
																							: singlePo?.isSet
																								? 'Set'
																								: 'Set/Pack'
																					}
																					disabled={true}
																				/>
																				<div className="select-component">
																					<div className="form-group size36 mb-2">
																						<div className="select-component">
																							<input
																								type="number"
																								onKeyPress={
																									validateNumber
																								}
																								className="w-100"
																								placeholder="Write qty"
																								onChange={(
																									e
																								) =>
																									onChangePO(
																										orderStyles,
																										singlePo.id,
																										'unitOfMeasurementQty',
																										e
																											.target
																											.value
																									)
																								}
																								disabled={
																									singleSetPackStyle !==
																										'PACK_STYLE' &&
																									!singlePo.isEdit
																								}
																								value={
																									singlePo.unitOfMeasurementQty
																								}
																							/>
																						</div>
																					</div>
																				</div>
																			</div>
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group composition-item mb-0">
																			<label>Deal</label>
																			<div className="d-flex composition-and-qty">
																				<div className="w-100">
																					<CustomDropdown
																						title="Select"
																						selectedItem={
																							(
																								singlePo?.projectDeal ||
																								projectDealList?.find(
																									(item) =>
																										item.dealId ===
																										singlePo?.dealId
																								) ||
																								{}
																							).dealName || ''
																						}
																						items={formatProjectDealOptions(
																							projectDealList
																						)}
																						toolTipLength={25}
																						isDisabled={
																							!singlePo.isEdit
																						}
																						onItemClick={(
																							value
																						) =>
																							handleDealChange(
																								singlePo.id,
																								value,
																								index
																							)
																						}
																					/>
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													)}
												</div>
											</div>
											<ColorSizeBreakDownTable
												item={singlePo}
												stylesData={orderStyles}
												setOrderStyles={setOrderStyles}
												quoteId={singlePo.id}
												isDrafted={isDrafted}
												error={error}
												errorDesign={errorDesign}
												//isOverflow={isOverflow}
												setIsOverflow={setIsOverflow}
												poItemIdentifier={poItemIdentifier}
												//isDisabled={!item?.isBaseRfq || !item.isEdit}
												// isDisabledTable={!item.isEdit}
												pOSegmentationType={pOSegmentationType}
												defaultPriceType={basicInfoData?.selectedIncoTerm?.value}
												isFromPoAutomation={isFromPoAutomation}
												dropDownselectedProductId={singlePo.productId}
											/>
											{rfqIdWiseEtdApproval[singlePo.id] && (
												<div className="style-etd-approval mt-3">
													<EtdApprovalInfo
														etdApprovalInfo={rfqIdWiseEtdApproval[singlePo.id]}
														getEtdApprovalInfo={getEtdApprovalInfo}
														onActionDone={() => getOrderQuotes(orderId)}
													/>
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					))}

					{openStyleModal && renderRightSidePanel()}
					<div className="footer-button d-flex justify-content-end">
						{!poSubmitButton && isDrafted && (
							<button
								className="button outline size32"
								data-toggle="modal"
								data-target="#add-multiple-design"
								ref={buttonRef}
								onClick={() => setOpenStyleModal(true)}
							>
								<CommonImage src="/icons/plus-20.svg" alt="" />
								<span className="ml-1">Style</span>
							</button>
						)}
						<button
							className={`button gray-outline size32 ml-2 ${poSubmitButton ? '' : 'd-none'}`}
							data-toggle="modal"
							data-target="#add-multiple-design"
							ref={buttonRef}
							onClick={() => setOpenStyleModal(true)}
						>
							<img src={`${process.env.PUBLIC_URL}/icons/plus-20.svg`} alt="add" />
							<span className="ml-1 gray_dark_01">Style</span>
						</button>
						{!poSubmitButton && isDrafted && (
							<button
								disabled={isDisabledSubmitButton(orderStyles)}
								className="button size32 ml-2"
								onClick={onSave}
							>
								Submit
							</button>
						)}

						{poSubmitButton && (
							<button className="button size32 ml-2" onClick={onPoSubmit}>
								PO Submit
							</button>
						)}
					</div>
				</div>
				<OrderCancelBottomBar selectedRFQIds={selectedRFQIds} setSelectedRFQIds={setSelectedRFQIds} />
				{/* <AddStyleFromExistingOrder
					newlyAddedStyle={newlyAddedStyles}
					orderId={orderId}
					onAddSuccess={onAddSuccess}
					onRemoveStyles={onRemoveStyles}
				/> */}
			</>
			{dealSplitModal}
			<PlaceOrderConfirmation
				show={show}
				onPlaceOrder={createOrder}
				orderStyles={orderStyles}
				handleClose={handleClose}
				isPlacingOrder={isPlacingOrder}
			/>
			{showProgramTypeModal && (
				<ModalContainer onCloseModal={closeProgramTypeModal}>
					<div className="common-popup shadow-24dp">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Update program type</h3>
							</div>
							<div className="close-btn" onClick={closeProgramTypeModal}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="form-group mb-0">
								<label htmlFor="programTypeName">Program type name</label>
								<input
									id="programTypeName"
									type="text"
									autoComplete="off"
									value={programTypeName}
									onChange={(e) => setProgramTypeName(e.target.value)}
									placeholder="Write program type name"
								/>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32"
									onClick={closeProgramTypeModal}
									disabled={isProgramTypeSubmitting}
								>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									onClick={onUpdateProgramType}
									disabled={!programTypeName.trim() || isProgramTypeSubmitting}
								>
									Update
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{selectedPo && <EtdUpdateLog onClose={() => setSelectedPo(null)} onSubmit={submitETDLog} />}
		</>
	);
};

export default OrderStyles;
